import { updateAdvertisingFilterStateForSheet, filterStateManager, initializeFilterStateManager, AdvertisingFilterState } from "../shared/filterStateManager";
import { fetchMockMarketingData, fetchFilteredAdvertisingData } from "../shared/mockData";
import { setBordersOnRange } from "./dashboard";

async function generateMarketingDashboard(filteredData?: any, appliedFilters?: Partial<AdvertisingFilterState>) {
  try {
    await Excel.run(async (context) => {
      console.log("createMarketingDashboard function called from ribbon");
      let marketingDataSheet: Excel.Worksheet;
      const worksheets = context.workbook.worksheets;
      worksheets.load("items/name");
      await context.sync();
  
      // Check if Advertising Dashboard worksheet exists and delete it
      let rawDataExists = false;
      worksheets.items.forEach((sheet) => {
        if (sheet.name === "Advertising Dashboard") {
          rawDataExists = true;
          marketingDataSheet = sheet;
        }
      });
  
      if (rawDataExists) {
        console.log("📝 Advertising Dashboard worksheet exists, deleting...");
        marketingDataSheet!.delete();
        await context.sync();
      }
  
      console.log("➕ Creating new Advertising Dashboard worksheet...");
      marketingDataSheet = worksheets.add("Advertising Dashboard");
      marketingDataSheet.load("name");
      marketingDataSheet.activate();
      await context.sync();

      // Ensure filter state manager ready
      if (!filterStateManager.isInitialized()) {
        await filterStateManager.initialize();
      }
      const currentName = marketingDataSheet.name;
      
      // Use filtered data if provided, otherwise fetch last 30 days by default
      let marketingData;
      let filtersToSave: any = null;
      
      if (filteredData && appliedFilters) {
        // Filters were applied - use the provided filtered data and save the applied filters
        marketingData = filteredData;
        filtersToSave = {
          startDate: appliedFilters.startDate || null,
          endDate: appliedFilters.endDate || null,
          brand: appliedFilters.brand || [],
          skus: appliedFilters.skus || [],
          channel: appliedFilters.channel || '',
          keyword: appliedFilters.keyword || [],
          campaign: appliedFilters.campaign || [],
        };
        console.log("💾 Saving applied filters to new sheet:", filtersToSave);
      } else {
        // Try to hydrate existing filters first
        const hydratedFilters = await filterStateManager.hydrateAdvertisingFiltersForSheet(currentName);
        
        // Check if we have valid date filters (not empty defaults)
        const hasExistingFilters = hydratedFilters.startDate && hydratedFilters.endDate;
        
        let defaultFilters : any = {};
        if (hasExistingFilters) {
          // Use existing filters from custom properties
          defaultFilters = {
            startDate: hydratedFilters.startDate instanceof Date 
              ? hydratedFilters.startDate.toISOString().split('T')[0]
              : hydratedFilters.startDate,
            endDate: hydratedFilters.endDate instanceof Date
              ? hydratedFilters.endDate.toISOString().split('T')[0]
              : hydratedFilters.endDate,
            brand: hydratedFilters.brand || [],
            skus: hydratedFilters.skus || [],
            channel: hydratedFilters.channel || '',
            keyword: hydratedFilters.keyword || [],
            campaign: hydratedFilters.campaign || [],
          };
        } else {
          // Check legacy storage (old way)
          const existingFilter = await OfficeRuntime.storage.getItem(`advertising_filter_${currentName}`);
          if (existingFilter) {
            const existingFilterParams = JSON.parse(existingFilter);
            defaultFilters = {
              startDate: existingFilterParams.startDate,
              endDate: existingFilterParams.endDate,
              brand: existingFilterParams.brand || [],
              skus: existingFilterParams.skus || [],
              channel: existingFilterParams.channel || '',
              keyword: existingFilterParams.keyword || [],
              campaign: existingFilterParams.campaign || [],
            };
          } else {
            // No existing filters - default to two days ago through yesterday
            const endDateObj = new Date();
            endDateObj.setDate(endDateObj.getDate() - 1);
            const startDateObj = new Date();
            startDateObj.setDate(startDateObj.getDate() - 2);
            defaultFilters = {
              startDate: startDateObj.toISOString().split('T')[0],
              endDate: endDateObj.toISOString().split('T')[0]
            };
          }
        }

        console.log("Generating advertising dashboard with filters:", defaultFilters);
        
        // Prepare filters to save
        filtersToSave = {
          startDate: new Date(defaultFilters.startDate),
          endDate: new Date(defaultFilters.endDate),
          brand: defaultFilters.brand || [],
          skus: defaultFilters.skus || [],
          channel: defaultFilters.channel || '',
          keyword: defaultFilters.keyword || [],
          campaign: defaultFilters.campaign || [],
        };
        
        marketingData = await fetchFilteredAdvertisingData(defaultFilters);
      }
      
      // Save filters to custom properties AFTER sheet is created
      if (filtersToSave) {
        await filterStateManager.updateAdvertisingFilterStateForSheet(currentName, filtersToSave);
        console.log("✅ Filters saved to new sheet:", filtersToSave);
      }
      
      console.log("marketingData", marketingData);
  
      // Check if data is empty or missing
      if (!marketingData || !marketingData.data || marketingData.data.length === 0) {
        // Display a user-friendly message in the sheet
        const titleRange = marketingDataSheet.getRange("A1:K2");
        titleRange.merge();
        marketingDataSheet.getRange("A1").values = [["Advertising Dashboard"]];
        titleRange.format.font.size = 16;
        titleRange.format.font.bold = true;
        titleRange.format.horizontalAlignment = "Center";
        titleRange.format.verticalAlignment = "Center";
        titleRange.format.fill.color = "#F0F4F8";
        titleRange.format.font.color = "#1F4788";

        const messageRange = marketingDataSheet.getRange("A3:K6");
        messageRange.merge();
        marketingDataSheet.getRange("A3").values = [["⚠️ No Data Available"]];
        messageRange.format.font.size = 14;
        messageRange.format.font.bold = true;
        messageRange.format.horizontalAlignment = "Center";
        messageRange.format.verticalAlignment = "Center";
        messageRange.format.fill.color = "#FFF3CD";
        messageRange.format.font.color = "#856404";

        const detailRange = marketingDataSheet.getRange("A7:K9");
        detailRange.merge();
        marketingDataSheet.getRange("A7").values = [["No data found for the selected filters. Please try adjusting your date range or filter criteria."]];
        detailRange.format.font.size = 11;
        detailRange.format.horizontalAlignment = "Center";
        detailRange.format.verticalAlignment = "Center";
        detailRange.format.font.color = "#666666";
        detailRange.format.wrapText = true;

        await context.sync();
        throw new Error("No data available for the selected filters. Please try adjusting your date range or filter criteria.");
      }
  
      // Title
      const titleRange = marketingDataSheet.getRange("A1:K2");
      titleRange.merge();
      marketingDataSheet.getRange("A1").values = [["Advertising Dashboard"]];
      titleRange.format.font.size = 16;
      titleRange.format.font.bold = true;
      titleRange.format.horizontalAlignment = "Center";
      titleRange.format.verticalAlignment = "Center";
      titleRange.format.fill.color = "#F0F4F8";
      titleRange.format.font.color = "#1F4788";

      // Display applied filters
      try {
        if (!filterStateManager.isInitialized()) {
          await filterStateManager.initialize();
        }
        const params = await filterStateManager.hydrateAdvertisingFiltersForSheet(marketingDataSheet.name);
        let filterTexts = [];
        
        if (params.startDate && params.endDate) {
          const formatDateMMDDYYYY = (dateStr: string) => {
            const date = new Date(dateStr);
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const year = date.getFullYear();
            return `${month}/${day}/${year}`;
          };
          const startDate = formatDateMMDDYYYY(typeof params.startDate === 'string' ? params.startDate : params.startDate?.toISOString() || '');
          const endDate = formatDateMMDDYYYY(typeof params.endDate === 'string' ? params.endDate : params.endDate?.toISOString() || '');
          filterTexts.push(`Date: ${startDate} - ${endDate}`);
        }
        
        if (params.brand && Array.isArray(params.brand) && params.brand.length > 0) {
          filterTexts.push(`Brand: ${params.brand.join(', ')}`);
        }
        
        if (params.skus && Array.isArray(params.skus) && params.skus.length > 0) {
          filterTexts.push(`SKU: ${params.skus.join(', ')}`);
        }
        
        if (params.channel && params.channel !== 'all') {
          const channelNames = {
            amazon_sp: "Amazon Sponsored Products",
            amazon_sb: "Amazon Sponsored Brands",
            amazon_sd: "Amazon Sponsored Display",
            google_shopping: "Google Shopping",
            google_search: "Google Search"
          };
          const channelName = channelNames[params.channel] || params.channel;
          filterTexts.push(`Channel: ${channelName}`);
        }
        
        if (params.keyword && Array.isArray(params.keyword) && params.keyword.length > 0) {
          filterTexts.push(`Keyword: ${params.keyword.join(', ')}`);
        }
        
        if (params.campaign && Array.isArray(params.campaign) && params.campaign.length > 0) {
          filterTexts.push(`Campaign: ${params.campaign.join(', ')}`);
        }
        
        if (filterTexts.length > 0) {
          const filterText = `Applied Filters: ${filterTexts.join(" | ")}`;
          const truncatedText = filterText.length > 100 ? `${filterText.slice(0, 100).trimEnd()}...` : filterText;
          const filterRange = marketingDataSheet.getRange("A3:K3");
          filterRange.merge();
          marketingDataSheet.getRange("A3").values = [[truncatedText]];
          filterRange.format.font.size = 10;
          filterRange.format.font.italic = true;
          filterRange.format.font.color = "#666666";
          filterRange.format.horizontalAlignment = "Center";
          filterRange.format.verticalAlignment = "Center";
          filterRange.format.fill.color = "#F0F4F8";
        }
      } catch (error) {
        console.warn("Could not load advertising filters for dashboard header:", error);
      }
  
      // Get column indices
      const impressionIndex = marketingData.columns.indexOf("impressions");
      const clickIndex = marketingData.columns.indexOf("clicks");
      const costIndex = marketingData.columns.indexOf("cost");
      const salesIndex = marketingData.columns.indexOf("sales_7d");
      const sales14DIndex = marketingData.columns.indexOf("sales_14d");
      const partitionStartIndex = marketingData.columns.indexOf("partition_start");
      const purchases7DIndex = marketingData.columns.indexOf("purchases_7d");
      const campaignIndex = marketingData.columns.indexOf("campaign_name");
      const searchTermIndex = marketingData.columns.indexOf("search_term");
  
      // Calculate totals
      const impRows = marketingData.data.map(row => row[impressionIndex] || 0);
      const impTotal = impRows.reduce((acc, curr) => acc + curr, 0).toFixed(0);
      const clickRows = marketingData.data.map(row => row[clickIndex] || 0);
      const clickTotal = clickRows.reduce((acc, curr) => acc + curr, 0).toFixed(0);
      const costRows = marketingData.data.map(row => row[costIndex] || 0);
      const costTotal = costRows.reduce((acc, curr) => acc + curr, 0).toFixed(0);
      const salesRows = marketingData.data.map(row => row[salesIndex] || 0);
      const salesTotal = salesRows.reduce((acc, curr) => acc + curr, 0).toFixed(0);
      const purchases7DRows = marketingData.data.map(row => row[purchases7DIndex] || 0);
      const purchases7DTotal = purchases7DRows.reduce((acc, curr) => acc + curr, 0).toFixed(0);
      const partitionStartRows = marketingData.data.map(row => row[partitionStartIndex] || '');

      // Compute KPIs
      const totalCostNum = parseFloat(costTotal) || 0;
      const totalSalesNum = parseFloat(salesTotal) || 0;
      const totalClicksNum = parseFloat(clickTotal) || 0;
      const totalPurchasesNum = parseFloat(purchases7DTotal) || 0;
      const roasOverall = totalCostNum > 0 ? totalSalesNum / totalCostNum : 0;
      const conversionOverall = totalClicksNum > 0 ? (totalPurchasesNum / totalClicksNum) * 100 : 0;
      const ctrOverall = parseFloat(impTotal) > 0 ? (totalClicksNum / parseFloat(impTotal)) * 100 : 0;
  
      // KPI Cards Row 1
      const totalRange = marketingDataSheet.getRange("A4:C6");
      totalRange.merge();
      marketingDataSheet.getRange("A4").values = [[`Total Impressions\n${impTotal}`]];
      totalRange.format.font.bold = true;
      totalRange.format.horizontalAlignment = "Center";
      totalRange.format.verticalAlignment = "Center";
      setBordersOnRange(totalRange, "#28A745", Excel.BorderWeight.thick);

      const clickRange = marketingDataSheet.getRange("D4:F6");
      clickRange.merge();
      marketingDataSheet.getRange("D4").values = [[`Total Clicks\n${clickTotal}`]];
      clickRange.format.font.bold = true;
      clickRange.format.horizontalAlignment = "Center";
      clickRange.format.verticalAlignment = "Center";
      setBordersOnRange(clickRange, "#2196F3", Excel.BorderWeight.thick);

      const costRange = marketingDataSheet.getRange("G4:H6");
      costRange.merge();
      marketingDataSheet.getRange("G4").values = [[`Total Cost\n$${costTotal}`]];
      costRange.format.font.bold = true;
      costRange.format.horizontalAlignment = "Center";
      costRange.format.verticalAlignment = "Center";
      setBordersOnRange(costRange, "#FFC107", Excel.BorderWeight.thick);

      const salesRange = marketingDataSheet.getRange("I4:K6");
      salesRange.merge();
      marketingDataSheet.getRange("I4").values = [[`Total Sales\n$${salesTotal}`]];
      salesRange.format.font.bold = true;
      salesRange.format.horizontalAlignment = "Center";
      salesRange.format.verticalAlignment = "Center";
      setBordersOnRange(salesRange, "#9C27B0", Excel.BorderWeight.thick);

      // *** COLUMN WIDTH SETTINGS - Column C expanded to 120 ***
      marketingDataSheet.getRange("C:C").format.columnWidth = 120;
      marketingDataSheet.getRange("I:I").format.columnWidth = 120;
      marketingDataSheet.getRange("J:J").format.columnWidth = 120;
      marketingDataSheet.getRange("K:K").format.columnWidth = 120;

      // KPI Cards Row 2
      const roasRange = marketingDataSheet.getRange("A7:D8");
      roasRange.merge();
      marketingDataSheet.getRange("A7").values = [[`ROAS\n${roasOverall.toFixed(2)}x`]];
      roasRange.format.font.bold = true;
      roasRange.format.horizontalAlignment = "Center";
      roasRange.format.verticalAlignment = "Center";
      setBordersOnRange(roasRange, "#4CAF50", Excel.BorderWeight.thick);

      const convRange = marketingDataSheet.getRange("E7:H8");
      convRange.merge();
      marketingDataSheet.getRange("E7").values = [[`Conversion Rate\n${conversionOverall.toFixed(2)}%`]];
      convRange.format.font.bold = true;
      convRange.format.horizontalAlignment = "Center";
      convRange.format.verticalAlignment = "Center";
      setBordersOnRange(convRange, "#FF5722", Excel.BorderWeight.thick);

      const ctrRange = marketingDataSheet.getRange("I7:K8");
      ctrRange.merge();
      marketingDataSheet.getRange("I7").values = [[`CTR\n${ctrOverall.toFixed(2)}%`]];
      ctrRange.format.font.bold = true;
      ctrRange.format.horizontalAlignment = "Center";
      ctrRange.format.verticalAlignment = "Center";
      setBordersOnRange(ctrRange, "#00BCD4", Excel.BorderWeight.thick);

      let chartRow = 10;

        // ====== CHART 1: Campaign Performance (Cost vs Sales) ======
        const salesVsCostHeaders = ["Campaign Name", "Cost", "Sales 7D", "Sales 14D"];
        let salesVsCostData = marketingData.data.map(row => [
          row[campaignIndex],
          row[costIndex] || 0,
          row[salesIndex] || 0,
          row[sales14DIndex] || 0
        ]);
        salesVsCostData = salesVsCostData.sort((a, b) => b[3] - a[3]).slice(0, 10);

        const headerRange = marketingDataSheet.getRange("AA1:AD1");
        headerRange.values = [salesVsCostHeaders];
        headerRange.format.fill.color = "#4472C4";
        headerRange.format.font.color = "white";
        headerRange.format.font.bold = true;
        headerRange.format.horizontalAlignment = "Center";

        if (salesVsCostData.length > 0) {
          const salesVsCostDataRange = marketingDataSheet.getRange(`AA2:AD${salesVsCostData.length + 1}`);
          salesVsCostDataRange.values = salesVsCostData;
          
          const barChart = marketingDataSheet.charts.add(
            Excel.ChartType.columnClustered,
            marketingDataSheet.getRange(`AA1:AD${salesVsCostData.length + 1}`)
          );
          
          barChart.setPosition(`A${chartRow}`, `K${chartRow + 18}`);
          barChart.title.text = "Campaign Performance: Cost vs Sales";
          barChart.axes.valueAxis.title.text = "USD";
          barChart.axes.categoryAxis.title.text = "Campaign Name";
          
          barChart.axes.categoryAxis.format.font.size = 9;
          
          const barSeries = barChart.series;
          barSeries.getItemAt(0).name = "Cost";
          barSeries.getItemAt(1).name = "Sales 7D";
          barSeries.getItemAt(2).name = "Sales 14D";

          chartRow += 20;
        }

      // ====== CHART 2: ROAS Waterfall ======
      const roasHeaders = ["Campaign Name", "ROAS"];
      const campaignAgg: { [k: string]: { cost: number; sales: number } } = {};
      marketingData.data.forEach((row) => {
        const cName = row[campaignIndex];
        const cCost = Number(row[costIndex]) || 0;
        const cSales = Number(row[salesIndex]) || 0;
        if (!campaignAgg[cName]) {
          campaignAgg[cName] = { cost: 0, sales: 0 };
        }
        campaignAgg[cName].cost += cCost;
        campaignAgg[cName].sales += cSales;
      });

      const roasRows: any[] = Object.entries(campaignAgg)
        .map(([name, v]) => [name, v.cost > 0 ? v.sales / v.cost : 0])
        .sort((a, b) => Number(b[1]) - Number(a[1]))
        .slice(0, 10);

      if (roasRows.length > 0) {
        const roasHeaderRange = marketingDataSheet.getRange("AL1:AM1");
        roasHeaderRange.values = [roasHeaders];
        roasHeaderRange.format.fill.color = "#4472C4";
        roasHeaderRange.format.font.color = "white";
        roasHeaderRange.format.font.bold = true;
        roasHeaderRange.format.horizontalAlignment = "Center";

        const roasDataRange = marketingDataSheet.getRange(`AL2:AM${roasRows.length + 1}`);
        roasDataRange.values = roasRows;

        const roasChart = marketingDataSheet.charts.add(
          Excel.ChartType.waterfall,
          marketingDataSheet.getRange(`AL1:AM${roasRows.length + 1}`)
        );
        roasChart.setPosition(`A${chartRow}`, `K${chartRow + 18}`);
        roasChart.title.text = "ROAS by Campaign (Waterfall)";
        roasChart.axes.categoryAxis.title.text = "Campaign Name";
        roasChart.axes.valueAxis.title.text = "ROAS Ratio";

        chartRow += 20;
      }

          // ====== CHART 3: Conversion Funnel (Percentage-Based) ======
          const totalImpressions = parseFloat(impTotal);
          const totalClicks = parseFloat(clickTotal);
          const totalPurchases = parseFloat(purchases7DTotal);

          // Calculate percentages relative to impressions (baseline = 100%)
          const clickPercentage = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100) : 0;
          const purchasePercentage = totalImpressions > 0 ? ((totalPurchases / totalImpressions) * 100) : 0;

          // Calculate drop-off rates
          const clickDropOff = 100 - clickPercentage;
          const purchaseDropOff = clickPercentage - purchasePercentage;

          const funnelHeaders = ["Stage", "Converted (%)", "Drop-off (%)"];
          const funnelData = [
            ["Impressions", 100, 0],
            ["Clicks", parseFloat(clickPercentage.toFixed(2)), parseFloat(clickDropOff.toFixed(2))],
            ["Purchases", parseFloat(purchasePercentage.toFixed(2)), parseFloat(purchaseDropOff.toFixed(2))]
          ];

          const funnelHeaderRange = marketingDataSheet.getRange("AF1:AH1");
          funnelHeaderRange.values = [funnelHeaders];
          funnelHeaderRange.format.fill.color = "#4472C4";
          funnelHeaderRange.format.font.color = "white";
          funnelHeaderRange.format.font.bold = true;
          funnelHeaderRange.format.horizontalAlignment = "Center";

          const funnelDataRange = marketingDataSheet.getRange(`AF2:AH${funnelData.length + 1}`);
          funnelDataRange.values = funnelData;

          const funnelChart = marketingDataSheet.charts.add(
            Excel.ChartType.barStacked100,
            marketingDataSheet.getRange(`AF1:AH${funnelData.length + 1}`)
          );
          funnelChart.setPosition(`A${chartRow}`, `F${chartRow + 18}`);
          funnelChart.title.text = "Conversion Funnel: Retention vs Drop-off Rate";
          funnelChart.axes.categoryAxis.reversePlotOrder = true;
          funnelChart.legend.visible = true;
          funnelChart.legend.position = Excel.ChartLegendPosition.bottom;

      // ====== CHART 4: Time Series (Daily Spend vs Revenue) ======
      const dailyData: { [date: string]: { cost: number; sales: number } } = {};
      marketingData.data.forEach(row => {
        const dateStr = row[partitionStartIndex];
        if (!dateStr) return;
        const cCost = Number(row[costIndex]) || 0;
        const cSales = Number(row[salesIndex]) || 0;
        if (!dailyData[dateStr]) {
          dailyData[dateStr] = { cost: 0, sales: 0 };
        }
        dailyData[dateStr].cost += cCost;
        dailyData[dateStr].sales += cSales;
      });

      const sortedDates = Object.keys(dailyData).sort();
      const timeSeriesData = sortedDates.map(date => [
        date,
        dailyData[date].cost,
        dailyData[date].sales
      ]);

      const timeSeriesHeaders = ["Date", "Cost", "Sales"];
      const tsHeaderRange = marketingDataSheet.getRange("AI1:AK1");
      tsHeaderRange.values = [timeSeriesHeaders];
      tsHeaderRange.format.fill.color = "#4472C4";
      tsHeaderRange.format.font.color = "white";
      tsHeaderRange.format.font.bold = true;
      tsHeaderRange.format.horizontalAlignment = "Center";

      const tsDataRange = marketingDataSheet.getRange(`AI2:AK${timeSeriesData.length + 1}`);
      tsDataRange.values = timeSeriesData;

      const timeSeriesChart = marketingDataSheet.charts.add(
        Excel.ChartType.line,
        marketingDataSheet.getRange(`AI1:AK${timeSeriesData.length + 1}`)
      );
      timeSeriesChart.setPosition(`G${chartRow}`, `K${chartRow + 18}`);
      timeSeriesChart.title.text = "Daily Spend vs Revenue Trend";
      timeSeriesChart.axes.valueAxis.title.text = "USD";
      timeSeriesChart.axes.categoryAxis.title.text = "Date";
      timeSeriesChart.legend.visible = true;

      chartRow += 20;


      // ====== CHART 5: Search Term Performance Bubble Chart (Scatter) ======
      const searchTermAgg: { [term: string]: { cost: number; sales: number; impressions: number; clicks: number } } = {};
      marketingData.data.forEach(row => {
        const term = row[searchTermIndex];
        if (!term || term === '') return;
        const cCost = Number(row[costIndex]) || 0;
        const cSales = Number(row[salesIndex]) || 0;
        const cImpressions = Number(row[impressionIndex]) || 0;
        const cClicks = Number(row[clickIndex]) || 0;
        if (!searchTermAgg[term]) {
          searchTermAgg[term] = { cost: 0, sales: 0, impressions: 0, clicks: 0 };
        }
        searchTermAgg[term].cost += cCost;
        searchTermAgg[term].sales += cSales;
        searchTermAgg[term].impressions += cImpressions;
        searchTermAgg[term].clicks += cClicks;
      });

      const searchTermRows = Object.entries(searchTermAgg)
        .map(([term, v]) => [
          term, 
          Number(v.cost), 
          Number(v.sales),
          Number(v.impressions),
          Number(v.cost) > 0 ? Number(v.sales) / Number(v.cost) : 0
        ])
        .filter(item => Number(item[1]) > 0)
        .sort((a, b) => Number(b[2]) - Number(a[2]))
        .slice(0, 15);

      if (searchTermRows.length > 0) {
        const stHeaders = ["Search Term", "Cost", "Sales", "Impressions", "ROAS"];
        const stHeaderRange = marketingDataSheet.getRange("AT1:AX1");
        stHeaderRange.values = [stHeaders];
        stHeaderRange.format.fill.color = "#4472C4";
        stHeaderRange.format.font.color = "white";
        stHeaderRange.format.font.bold = true;
        stHeaderRange.format.horizontalAlignment = "Center";

        const stDataRange = marketingDataSheet.getRange(`AT2:AX${searchTermRows.length + 1}`);
        stDataRange.values = searchTermRows;

        const searchTermChart = marketingDataSheet.charts.add(
          Excel.ChartType.xyscatter,
          marketingDataSheet.getRange(`AU1:AW${searchTermRows.length + 1}`)
        );
        searchTermChart.setPosition(`A${chartRow}`, `K${chartRow + 18}`);
        searchTermChart.title.text = "Top 15 Search Terms: Cost vs Sales";
        searchTermChart.axes.valueAxis.title.text = "Sales (USD)";
        searchTermChart.axes.categoryAxis.title.text = "Cost (USD)";
        searchTermChart.legend.visible = false;

        chartRow += 20;
      }
      await context.sync();
    });
  } catch (error) {
    console.log('error', error);
    throw error;
  }
}

export { generateMarketingDashboard };